import cropStartpageImage from './startpage_crop.js';

export default {
  startpageForm() {
    return this.element.querySelector('#startpage form');
  },

  loadStartpageRichText(element, html) {
    const input = document.getElementById(element.getAttribute('input'));
    if (input) input.value = html || '';
    const load = () => element.editor.loadHTML(html || '');
    if (element.editor) load();
    else element.addEventListener('trix-initialize', load, {once: true});
  },

  initializeStartpageLanguageTabs() {
    const form = this.startpageForm();
    const tabs = Array.from(form.querySelectorAll('button[data-bs-target^="#language_"]'));
    const showLanguage = tab => {
      form.querySelectorAll('[id^="language_"]').forEach(pane => {
        const active = `#${pane.id}` === tab.getAttribute('data-bs-target');
        pane.hidden = !active;
        pane.classList.toggle('active', active);
        pane.classList.toggle('show', active);
      });
    };
    tabs.forEach(tab => {
      if (tab.dataset.startpageLanguageBound) return;
      tab.dataset.startpageLanguageBound = 'true';
      tab.addEventListener('shown.bs.tab', () => showLanguage(tab));
    });
    const activeTab = tabs.find(tab => tab.classList.contains('active')) || tabs[0];
    if (activeTab) showLanguage(activeTab);
  },

  populateStartpageAppearance(data) {
    const form = this.startpageForm();
    if (!form) return;
    this.startpageLocales = data.system.locales;
    const values = Object.fromEntries(data.config.map(entry => [entry.attr, entry.value]));
    const appearance = values['start.appearance'] || {};
    form.querySelector('#startpage_header_mode').value = appearance.header_mode || 'default';
    form.querySelector('#startpage_shop_name').value = appearance.shop_name || '';
    form.querySelector('#startpage_font_family').value = appearance.font_family || '';
    form.querySelector('#startpage_slideshow_autoplay').checked = appearance.slideshow_autoplay !== false;
    form.querySelector('#startpage_slideshow_interval').value = appearance.slideshow_interval || 8;
    this.initializeStartpageLanguageTabs();
    form.querySelector('[data-settings-target="startpageAppearance"]').disabled = false;
    this.clearStartpagePreviews(form);
    this.startpageFiles = {};
    this.startpageImageReplacements = {};
    form.querySelectorAll('input[type=file]').forEach(input => { input.value = ''; });
    form.querySelectorAll('input[name^="startpage[remove_"]').forEach(input => { input.checked = false; });
    form.querySelectorAll('input[type=color][data-color-field]').forEach(picker => {
      const input = form.querySelector(`#startpage_color_${picker.dataset.colorField}`);
      input.value = (appearance.colors || {})[picker.dataset.colorField] || '';
      picker.value = input.value || picker.dataset.defaultColor;
    });
    data.system.locales.forEach(locale => {
      const pane = form.querySelector(`#language_${locale}`);
      if (!pane) return;
      pane.querySelectorAll('[data-startpage-metas]').forEach(element => element.remove());
      const fields = document.createElement('div');
      fields.className = 'row mt-4'; fields.dataset.startpageMetas = '';
      ['title', 'meta_description', 'social_image_alt'].forEach(field => {
        const column = document.createElement('div'); column.className = field === 'social_image_alt' ? 'col-12 mb-3' : 'col-12 col-md-6 mb-3';
        const label = document.createElement('label'); label.className = 'form-label';
        label.htmlFor = `startpage_${field}_${locale}`;
        label.textContent = I18n.t(`webshop.startpage_settings.${field}`);
        const input = document.createElement(field === 'title' ? 'input' : 'textarea');
        input.className = 'form-control'; input.id = label.htmlFor;
        input.name = `startpage[texts][${locale}][${field}]`; input.maxLength = field === 'title' ? 200 : 500;
        input.value = values[`start.metas.${locale}.${field === 'meta_description' ? 'description' : field}`] || '';
        column.append(label, input); fields.append(column);
        if (field === 'social_image_alt') {
          const help = document.createElement('div'); help.className = 'form-text';
          help.textContent = I18n.t('webshop.startpage_settings.social_image_alt_help'); column.append(help);
        }
      });
      pane.prepend(fields);
    });
    const media = data.startpage_media || {};
    const images = media.header_images || [];
    const list = form.querySelector('[data-startpage-media="header_images"]');
    list.replaceChildren();
    images.forEach((image, index) => {
      const card = document.createElement('div'); card.className = 'border rounded p-2'; card.dataset.imageId = image.id;
      const url = (appearance.header_images || [])[index];
      if (url) card.append(this.startpagePreview(url, false));
      const name = document.createElement('div'); name.textContent = image.name;
      const label = document.createElement('label'); label.className = 'form-check mt-2';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox'; checkbox.className = 'form-check-input';
      checkbox.name = 'startpage[remove_header_images][]'; checkbox.value = image.id;
      label.append(checkbox, document.createTextNode(I18n.t('webshop.startpage_settings.remove'))); card.append(name, label);
      [-1, 1].forEach(direction => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'btn btn-sm btn-outline-secondary mt-2 me-1';
        button.textContent = direction < 0 ? '←' : '→';
        button.setAttribute('aria-label', I18n.t(`webshop.startpage_settings.${direction < 0 ? 'move_up' : 'move_down'}`));
        button.addEventListener('click', () => {
          const sibling = direction < 0 ? card.previousElementSibling : card.nextElementSibling;
          if (sibling && sibling.dataset.imageId) {
            direction < 0 ? list.insertBefore(card, sibling) : list.insertBefore(sibling, card); this.updateStartpageImageOrder();
          }
        });
        card.append(button);
      });
      if (image.crop_url && image.content_type !== 'image/gif') {
        card.append(this.startpageCropButton(card, () => card.dataset.cropPreview || image.crop_url, image.name, file => {
          this.startpageImageReplacements[image.id] = file;
          name.textContent = file.name;
        }));
      }
      list.append(card);
    });
    this.updateStartpageImageOrder();
    ['header_video', 'footer_image', 'navbar_logo', 'header_logo', 'social_image', 'favicon'].forEach(field => {
      const target = form.querySelector(`[data-startpage-media="${field}"]`); target.replaceChildren();
      if (appearance[field]) target.append(this.startpagePreview(appearance[field], field === 'header_video'));
      if (media[field]) { const name = document.createElement('div'); name.textContent = media[field].name; target.append(name); }
    });
  },

  startpagePreview(url, video) {
    const element = document.createElement(video ? 'video' : 'img'); element.src = url;
    element.style.cssText = 'max-width:180px;max-height:110px;object-fit:contain;display:block';
    if (video) { element.controls = true; element.preload = 'metadata'; } else element.alt = '';
    return element;
  },

  updateStartpageImageOrder() {
    const form = this.startpageForm();
    form.querySelector('#startpage_header_image_order').value = Array.from(form.querySelectorAll('[data-image-id]')).map(card => card.dataset.imageId).join(',');
  },

  clearStartpagePreviews(form) {
    form.querySelectorAll('[data-crop-preview]').forEach(element => { URL.revokeObjectURL(element.dataset.cropPreview); delete element.dataset.cropPreview; });
    form.querySelectorAll('[data-new-preview]').forEach(element => { URL.revokeObjectURL(element.dataset.newPreview); element.remove(); });
  },

  startpageCropButton(card, source, filename, onApply) {
    const button = document.createElement('button'); button.type = 'button'; button.className = 'btn btn-sm btn-outline-primary mt-2';
    button.textContent = I18n.t('webshop.startpage_settings.crop');
    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        await cropStartpageImage(source(), filename, file => {
          if (!card.isConnected) return;
          if (card.dataset.cropPreview) URL.revokeObjectURL(card.dataset.cropPreview);
          const url = URL.createObjectURL(file); card.dataset.cropPreview = url;
          card.querySelector('img').src = url; onApply(file);
        });
      } catch (_) { business.showAlert('danger', I18n.t('webshop.startpage_settings.crop_error'), false, 7000); }
      finally { button.disabled = false; }
    });
    return button;
  },

  previewStartpageFiles(event) {
    const input = event.target;
    this.clearStartpagePreviews(input.parentElement);
    delete this.startpageFiles[input.name];
    const files = Array.from(input.files);
    const video = input.accept === 'video/mp4';
    if (files.some(file => file.size > (video ? 30 : 10) * 1024 * 1024 || file.size === 0) || files.length > 20) {
      input.value = ''; business.showAlert('danger', I18n.t('webshop.startpage_settings.save_error'), false, 7000); return;
    }
    this.startpageFiles[input.name] = files;
    files.forEach((file, index) => {
      const url = URL.createObjectURL(file); const preview = document.createElement('div');
      preview.dataset.newPreview = url; preview.className = 'mt-2';
      const name = document.createElement('div'); name.textContent = file.name;
      preview.append(this.startpagePreview(url, video), name);
      if (input.id === 'startpage_header_images' && file.type !== 'image/gif') {
        preview.append(this.startpageCropButton(preview, () => preview.dataset.cropPreview || url, file.name, cropped => {
          this.startpageFiles[input.name][index] = cropped; name.textContent = cropped.name;
        }));
      }
      input.parentElement.append(preview);
    });
  },

  pickStartpageColor(event) {
    this.startpageForm().querySelector(`#startpage_color_${event.target.dataset.colorField}`).value = event.target.value;
  },

  syncStartpageColor(event) {
    if (/^#[0-9a-f]{6}$/i.test(event.target.value)) event.target.parentElement.querySelector('input[type=color]').value = event.target.value;
  },

  resetStartpageColor(event) {
    const field = event.currentTarget.dataset.colorField;
    this.startpageForm().querySelector(`#startpage_color_${field}`).value = '';
    const picker = this.startpageForm().querySelector(`input[type=color][data-color-field="${field}"]`);
    picker.value = picker.dataset.defaultColor;
  },

  saveStartpage(event) {
    event.preventDefault(); if (!this.startpageLocales) return;
    const form = event.currentTarget; if (!form.reportValidity()) return;
    const data = new FormData(form);
    data.set('startpage[slideshow_autoplay]', form.querySelector('#startpage_slideshow_autoplay').checked ? '1' : '0');
    Object.entries(this.startpageFiles || {}).forEach(([name, files]) => { data.delete(name); files.forEach(file => data.append(name, file)); });
    const removed = data.getAll('startpage[remove_header_images][]');
    Object.entries(this.startpageImageReplacements || {}).forEach(([id, file]) => {
      if (!removed.includes(id)) data.append(`startpage[header_image_replacements][${id}]`, file);
    });
    this.startpageLocales.forEach(locale => {
      ['header', 'description', 'footer'].forEach(field => data.append(`startpage[texts][${locale}][${field}]`, data.get(`start[${field}_${locale}]`) || ''));
    });
    const button = form.querySelector('button[type=submit]'); button.disabled = true;
    const sessionError = () => business.showAlert('danger', I18n.t('webshop.startpage_settings.session_error'), false, 9000);
    // An open form can retain an expired token after another sign-in.
    // Obtain the current token through the authenticated webshop configuration.
    Rails.ajax({
      url: `/${this.locale}/webshop/${this.uuid}/config`, type: 'get', dataType: 'json',
      success: config => {
        if (!config?.csrf_token) { sessionError(); button.disabled = false; return; }
        data.set('authenticity_token', config.csrf_token);
        Rails.ajax({
          url: form.action, type: 'post', data, dataType: 'json',
          success: response => { business.showAlert('success', response.message, false, 5000); this.loadShopConfiguration(this.uuid); },
          error: (response, status, xhr) => {
            if (xhr?.responseText?.includes('InvalidAuthenticityToken')) sessionError();
            else business.showAlert('danger', (response && response.message) || I18n.t('webshop.startpage_settings.save_error'), false, 7000);
          },
          complete: () => { button.disabled = false; }
        });
      },
      error: () => { sessionError(); button.disabled = false; }
    });
  }
}
